// react
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// internationalization
import { useTranslation } from 'react-i18next';

// tanstack
import { createFileRoute } from '@tanstack/react-router';

// material ui icons
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';

// material ui
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Container,
  Divider,
  Grid,
  IconButton,
  List,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';

// api
import {
  useGetAuthorizationsByAccountIdQuery,
  useGetDatahubAccountByIdQuery,
  usePostDatahubIntegrationAccountMutation,
  usePostDatahubIntegrationAccountRetryMutation,
} from '@/api/datahub-integration/datahubIntegrationApiSlice';

// components
import DatahubGuideDialog from '@/features/datahub-integration/DatahubGuideDialog';
import DatahubIntegrationAccountCard from '@/features/datahub-integration/DatahubIntegrationAccountCard';
import { buildDatahubPowerOfAttorneyUrl } from '@/features/datahub-integration/powerOfAttorney';
import { getSelectedAccount } from '@/redux/selectedAccountSlice';
import { showSnackbar } from '@/redux/snackbarSlice';
import ErrorBoundary from '@/routes/-ErrorBoundary';

interface DatahubIntegrationAccount {
  ReCalcAccountId: string | number;
  CustomerCVR: string;
  ImplementationStatus?: string;
  IsAuthorized?: boolean;
}

interface DatahubAuthorization {
  customerCVR: string | number;
}

type ReconciliationNotice = {
  severity: 'success' | 'info' | 'warning' | 'error';
  messageKey: string;
};

type ReconciliationAction = {
  type: 'create' | 'retry';
  run: () => Promise<unknown>;
};

function normalizeCvr(value: string | number | undefined | null) {
  return value === undefined || value === null ? '' : String(value);
}

function getAccountIdFromPath(pathname: string) {
  return pathname.split('/').filter(Boolean)[0];
}

function clearPoaReturnQueryParam() {
  const currentUrl = new URL(window.location.href);

  if (!currentUrl.searchParams.has('poa')) {
    return;
  }

  currentUrl.searchParams.delete('poa');
  window.history.replaceState({}, '', currentUrl.toString());
}

export const Route = createFileRoute('/$accountId/_user/datahub-integration/')({
  component: DatahubIntegrationPage,
  errorComponent: ErrorBoundary,
});

export function DatahubIntegrationPage() {
  const { t } = useTranslation('datahub-integration');
  const selectedAccount = useSelector(getSelectedAccount);
  const dispatch = useDispatch();

  const selectedAccountId =
    selectedAccount?.AccountId ?? getAccountIdFromPath(window.location.pathname);
  const hasSelectedAccountId =
    selectedAccountId !== undefined && selectedAccountId !== null;

  const {
    data: accountData,
    isLoading,
    isError,
    refetch: refetchAccounts,
  } = useGetDatahubAccountByIdQuery(selectedAccountId, {
    skip: !hasSelectedAccountId,
  });
  const account = accountData as DatahubIntegrationAccount[] | undefined;
  const integrationAccounts = account ?? [];

  const { refetch: refetchAuthorizations } =
    useGetAuthorizationsByAccountIdQuery(selectedAccountId, {
      skip: !hasSelectedAccountId,
    });

  const [postDatahubIntegrationAccount] =
    usePostDatahubIntegrationAccountMutation();
  const [postDatahubIntegrationAccountRetry] =
    usePostDatahubIntegrationAccountRetryMutation();
  const [guideOpen, setGuideOpen] = useState(false);
  const [isReconciling, setIsReconciling] = useState(false);
  const [hasHandledPoaReturn, setHasHandledPoaReturn] = useState(false);
  const [reconciliationNotice, setReconciliationNotice] =
    useState<ReconciliationNotice | null>(null);
  const [poaReturned] = useState(
    () => new URLSearchParams(window.location.search).get('poa') === 'returned',
  );

  const getGrantLink = (includeReturnUrl = true) => {
    if (!hasSelectedAccountId) return '';

    return buildDatahubPowerOfAttorneyUrl({
      accountId: selectedAccountId,
      includeReturnUrl,
    });
  };

  const handleExternalGrantLink = () => {
    window.open(getGrantLink(true), '_blank', 'noopener,noreferrer');
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(getGrantLink(false));
    dispatch(
      showSnackbar({
        message: t('give-access.link-copied'),
        severity: 'success',
      }),
    );
  };

  const reconcileAuthorizations = useCallback(async (trigger: 'return' | 'manual') => {
    if (!hasSelectedAccountId || isReconciling) {
      return;
    }

    setIsReconciling(true);
    setReconciliationNotice(null);

    try {
      const [accountsResult, authorizationsResult] = await Promise.all([
        refetchAccounts(),
        refetchAuthorizations(),
      ]);

      const currentAccounts = (accountsResult.data ??
        []) as DatahubIntegrationAccount[];
      const authorizations = (authorizationsResult.data ??
        []) as DatahubAuthorization[];

      const uniqueAuthorizations = Array.from(
        new Set(
          authorizations
            .map((authorization) => normalizeCvr(authorization.customerCVR))
            .filter(Boolean),
        ),
      );

      if (uniqueAuthorizations.length === 0) {
        setReconciliationNotice({
          severity: 'info',
          messageKey: 'return-flow.no-authorization',
        });
        return;
      }

      const accountByCvr = new Map(
        currentAccounts.map((integration) => [
          normalizeCvr(integration.CustomerCVR),
          integration,
        ]),
      );

      const actions = uniqueAuthorizations.flatMap<ReconciliationAction>((customerCvr) => {
        const existingIntegration = accountByCvr.get(customerCvr);

        if (!existingIntegration) {
          return [
            {
              type: 'create' as const,
              run: () =>
                postDatahubIntegrationAccount({
                  ReCalcAccountId: selectedAccountId,
                  CustomerCVR: customerCvr,
                  IncludeChildMeteringPoints: true,
                }).unwrap(),
            },
          ];
        }

        const implementationStatus =
          existingIntegration.ImplementationStatus?.toLowerCase() ?? '';
        const shouldRetry =
          !existingIntegration.IsAuthorized ||
          implementationStatus.includes('unavailableerror');

        if (!shouldRetry) {
          return [];
        }

        return [
          {
            type: 'retry' as const,
            run: () =>
              postDatahubIntegrationAccountRetry({
                recalc_account_id: existingIntegration.ReCalcAccountId,
                cvr_number: existingIntegration.CustomerCVR,
              }).unwrap(),
          },
        ];
      });

      const results = await Promise.allSettled(
        actions.map((action) => action.run()),
      );
      const createdCount = actions.filter(
        (action) => action.type === 'create',
      ).length;
      const retriedCount = actions.filter(
        (action) => action.type === 'retry',
      ).length;
      const failedCount = results.filter(
        (result) => result.status === 'rejected',
      ).length;

      await refetchAccounts();

      if (failedCount > 0 && createdCount + retriedCount === failedCount) {
        setReconciliationNotice({
          severity: 'error',
          messageKey: 'return-flow.error',
        });
        dispatch(
          showSnackbar({
            message: t('return-flow.error'),
            severity: 'error',
          }),
        );
        return;
      }

      if (failedCount > 0) {
        setReconciliationNotice({
          severity: 'warning',
          messageKey: 'return-flow.partial-success',
        });
        dispatch(
          showSnackbar({
            message: t('return-flow.partial-success'),
            severity: 'warning',
          }),
        );
        return;
      }

      if (createdCount > 0 || retriedCount > 0) {
        setReconciliationNotice({
          severity: 'success',
          messageKey: 'return-flow.success',
        });
        dispatch(
          showSnackbar({
            message: t('return-flow.success'),
            severity: 'success',
          }),
        );
        return;
      }

      setReconciliationNotice({
        severity: 'success',
        messageKey: 'return-flow.already-prepared',
      });
    } catch {
      setReconciliationNotice({
        severity: 'error',
        messageKey: 'return-flow.error',
      });
      dispatch(
        showSnackbar({
          message: t('return-flow.error'),
          severity: 'error',
        }),
      );
    } finally {
      setIsReconciling(false);

      if (trigger === 'return') {
        setHasHandledPoaReturn(true);
        clearPoaReturnQueryParam();
      }
    }
  }, [
    dispatch,
    hasSelectedAccountId,
    isReconciling,
    postDatahubIntegrationAccount,
    postDatahubIntegrationAccountRetry,
    refetchAccounts,
    refetchAuthorizations,
    selectedAccountId,
    t,
  ]);

  useEffect(() => {
    if (!poaReturned || hasHandledPoaReturn || !hasSelectedAccountId) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      void reconcileAuthorizations('return');
    }, 0);

    return () => window.clearTimeout(timeoutId);
  }, [
    hasHandledPoaReturn,
    hasSelectedAccountId,
    poaReturned,
    reconcileAuthorizations,
  ]);

  return (
    <Container maxWidth="md" sx={{ mt: 4 }}>
      <Stack spacing={3}>
        {/* Create Integration Card */}
        <Card>
          <CardHeader
            title={t('create-integration.title')}
            action={
              <IconButton onClick={() => setGuideOpen(true)} color="primary">
                <HelpOutlineIcon />
              </IconButton>
            }
          />
          <CardContent>
            <Stack spacing={2}>
              <Grid container spacing={2} alignItems="stretch">
                <Grid
                  size={{
                    xs: 12,
                    md: 5,
                  }}
                >
                  <Stack spacing={2} sx={{ height: '100%' }}>
                    <ListItemText
                      primary={t('give-access.title')}
                      secondary={t('give-access.description')}
                    />
                    <Box sx={{ flexGrow: 1 }} />
                    <Box>
                      <Button
                        variant="contained"
                        disabled={!hasSelectedAccountId}
                        onClick={() => handleExternalGrantLink()}
                      >
                        {t('give-access.button')}
                      </Button>
                    </Box>
                  </Stack>
                </Grid>
                <Grid
                  size={{
                    xs: 12,
                    md: 2,
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      position: 'relative',
                      height: '100%',
                      minHeight: { xs: '40px', md: 'auto' },
                    }}
                  >
                    <Divider
                      orientation="vertical"
                      sx={{
                        display: { xs: 'none', md: 'block' },
                        height: '100%',
                        position: 'absolute',
                      }}
                    />
                    <Divider
                      sx={{
                        display: { xs: 'block', md: 'none' },
                        width: '100%',
                        position: 'absolute',
                      }}
                    />
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        bgcolor: 'background.paper',
                        px: 2,
                        zIndex: 1,
                      }}
                    >
                      {t('give-access.or')}
                    </Typography>
                  </Box>
                </Grid>
                <Grid
                  size={{
                    xs: 12,
                    md: 5,
                  }}
                >
                  <Stack spacing={2} sx={{ height: '100%' }}>
                    <ListItemText
                      primary={t('give-access.copy-title')}
                      secondary={t('give-access.copy-description')}
                    />
                    <Box sx={{ flexGrow: 1 }} />
                    <Box>
                      <Button
                        variant="outlined"
                        disabled={!hasSelectedAccountId}
                        startIcon={<ContentCopyIcon />}
                        onClick={handleCopyLink}
                      >
                        {t('give-access.copy-button')}
                      </Button>
                    </Box>
                  </Stack>
                </Grid>
              </Grid>

              <Stack spacing={2} sx={{ mt: 3 }}>
                <Box>
                  <Button
                    variant="text"
                    disabled={!hasSelectedAccountId || isReconciling}
                    onClick={() => reconcileAuthorizations('manual')}
                  >
                    {isReconciling
                      ? t('return-flow.checking')
                      : t('return-flow.check-now')}
                  </Button>
                </Box>

                <Typography variant="body2" color="text.secondary">
                  {t('return-flow.delegate-help')}
                </Typography>
              </Stack>
            </Stack>
          </CardContent>
        </Card>

        {reconciliationNotice ? (
          <Alert severity={reconciliationNotice.severity}>
            {t(reconciliationNotice.messageKey)}
          </Alert>
        ) : null}

        {/* Existing Integrations Card */}
        <Card>
          <CardHeader
            title={t('integrations-list.title')}
            subheader={t('integrations-list.subtitle')}
          />
          <CardContent>
            {isLoading ? (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                {!isError && integrationAccounts.length > 0 ? (
                  <List sx={{ width: '100%' }}>
                    {integrationAccounts.map((a) => {
                      return (
                        <DatahubIntegrationAccountCard
                          key={a.CustomerCVR}
                          account={a}
                        />
                      );
                    })}
                  </List>
                ) : (
                  <Typography color="text.secondary" sx={{ py: 2 }}>
                    {t('integrations-list.no-integrations')}
                  </Typography>
                )}
              </>
            )}
          </CardContent>
        </Card>

        {/* Guide Dialog */}
        <DatahubGuideDialog
          open={guideOpen}
          onClose={() => setGuideOpen(false)}
        />
      </Stack>
    </Container>
  );
}
